import React, { useEffect, useState } from "react";
import { Sidebar } from "flowbite-react";
import * as HiIcons from "react-icons/hi";
import { RiAdminLine, RiBarChartGroupedLine } from "react-icons/ri";
import { FaBuildingColumns } from "react-icons/fa6";
import { HiChartPie, HiUser, HiUsers, HiViewBoards } from "react-icons/hi";
import { MdAssignment, MdGroups2, MdMessage, MdOutlineMenuBook } from "react-icons/md";
import { auth } from "../../../utils/firebase";
import { AiOutlineCluster } from "react-icons/ai";
import { TbReportAnalytics } from "react-icons/tb";
import { Link } from "react-router-dom";
import { MdOutlineAssessment } from "react-icons/md";
import {
  useGetAdminUsingEmailQuery,
  useGetCampusQuery,
  useGetModulesByCampusQuery,
  useGetStaffByEmailIdQuery,
} from "../../../graphql/generated.tsx";
import { MdOutlineApproval, MdOutlineSchedule } from "react-icons/md";

function LeftSideBar() {
  const [currentPage, setCurrentPage] = useState(window.location.pathname);
  const [user, setUser] = useState();
  const [email, setEmail] = useState();
  
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [hoverEnabled, setHoverEnabled] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        setEmail(user.email);
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const superAdminEmails = [
    "training@thebettertomorrow.in",
    "bettertomorrowoffi@gmail.com",
    "beta-admin@bt.in",
  ];
  const isSuperAdminEmail = email && superAdminEmails.includes(email);

  const userRole = user
    ? JSON.parse(user?.reloadUserInfo?.customAttributes).role
    : null;

  const { data: admin } = useGetAdminUsingEmailQuery({
    variables: { email },
    skip: userRole != "bt-admin",
  });

  const { data: staff } = useGetStaffByEmailIdQuery({
    variables: { email },
    skip: userRole != "staff",
  });

  const staffCampusId = staff?.getStaffByEmailId?.campusId;
  const { data: staffCampusData } = useGetCampusQuery({
    variables: { id: staffCampusId },
    skip: userRole !== "staff" || !staffCampusId,
  });

  const staffCampusName = staffCampusData?.campus?.name;
  const { data: staffModulesData } = useGetModulesByCampusQuery({
    variables: { campusName: staffCampusName },
    skip: userRole !== "staff" || !staffCampusName,
  });

  const hasStaffModules = (staffModulesData?.getModulesByCampus?.length || 0) > 0;

  useEffect(() => {
    const handleLocationChange = () => setCurrentPage(window.location.pathname);
    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  const handleMouseEnter = () => {
    if (hoverEnabled) {
      setSidebarExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (hoverEnabled) {
      setSidebarExpanded(false);
    }
  };

  const handleHamburgerClick = () => {
    setMobileSidebarOpen((prev) => !prev);
    setSidebarExpanded(true); 
    setHoverEnabled(false);
  };

  const handleNavItemClick = () => {
    if (isMobileOrTablet) {
      setMobileSidebarOpen(false);
    }
    setSidebarExpanded(false);
    setHoverEnabled(true);
  };

  const getSidebarItemClass = (path) =>
    `flex items-center gap-3 ${
      currentPage.startsWith(path) ? "bg-gray-100 dark:bg-gray-700" : ""
    }`;

  const customTheme = {
    root: {
      base: "h-full",
      inner:
        "h-full overflow-y-auto overflow-x-hidden rounded bg-white py-4 px-3 dark:bg-gray-800",
    },
  };

  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    window.innerWidth <= 1024
  );
  useEffect(() => {
    const handleResize = () => setIsMobileOrTablet(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const width = (isMobileOrTablet ? mobileSidebarOpen : sidebarExpanded)
      ? "22rem"
      : "4rem";
    document.documentElement.style.setProperty("--admin-sidebar-width", width);
  }, [sidebarExpanded, mobileSidebarOpen, isMobileOrTablet]);

  return (
    <div className="h-full lg:w-12 lg:flex-shrink-0">
       {isMobileOrTablet && mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-10 bg-gray-900/50 dark:bg-gray-900/80"
          onClick={() => setMobileSidebarOpen(false)}
        ></div>
      )}
      <Sidebar
        collapsed={isMobileOrTablet ? !mobileSidebarOpen : !sidebarExpanded}
        theme={customTheme}
        onMouseEnter={!isMobileOrTablet ? handleMouseEnter : undefined}
        onMouseLeave={!isMobileOrTablet ? handleMouseLeave : undefined}
        className={`admin-sidebar fixed top-0 left-0 z-20 flex flex-col flex-shrink-0 h-full pt-16 duration-300 border-r border-gray-200 lg:flex transition-all dark:border-gray-700`}
        style={{
          width: (isMobileOrTablet ? mobileSidebarOpen : sidebarExpanded) ? "22rem" : "4rem",
          transition: "width 0.3s",
        }}
      >
        <style>{`
          .admin-sidebar li > a {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 15px;
          }

          .admin-sidebar li > a > span:first-child {
            width: 28px;
            min-width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .admin-sidebar li > a svg {
            width: 22px;
            height: 22px;
          }
        `}</style>
        <Sidebar.Items>
          <Sidebar.ItemGroup>
            {isMobileOrTablet && (
              <div className="mb-2">
                <button
                  onClick={handleHamburgerClick}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ${
                    mobileSidebarOpen ? "bg-gray-200 text-gray-900" : "text-gray-600"
                  }`}
                >
                  {mobileSidebarOpen ? (
                    <HiIcons.HiX className="w-6 h-6" />
                  ) : (
                    <HiIcons.HiMenuAlt1 className="w-6 h-6" />
                  )}
                </button>
              </div>
            )}

            {/* {userRole === "staff" && (
                 <Sidebar.Item
                    as={Link}
                    to={"/staff-data-instruction"}
                    icon={MdOutlineMenuBook}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/staff-data-instruction")}
                  >
                    {sidebarExpanded && "Guide Lines"}
                  </Sidebar.Item>
            )} */}

            {userRole === "staff" && (
              <>
                <Sidebar.Item
                    as={Link}
                    to={"/admin/individual-report"}
                    icon={TbReportAnalytics}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/individual-report")}
                  >
                    {sidebarExpanded && "Student Dashboard"}
                  </Sidebar.Item>
                {/* Hidden Course Leaderboard (BatchAnalysis) - uncomment to restore */}
                {/* <Sidebar.Item
                  as={Link}
                  to={"/admin"}
                  icon={MdGroups2}
                  onClick={handleNavItemClick}
                  className={
                    currentPage.toLowerCase() === "/admin" || currentPage.toLowerCase() === "/admin/student"
                      ? "bg-gray-100 dark:bg-gray-700"
                      : ""
                  }
                >
                  {sidebarExpanded && "Course Leaderboard"}
                </Sidebar.Item> */}
                <Sidebar.Item
                    as={Link}
                    to={staffCampusName ? `/admin/reports/course/${staffCampusName}` : "/admin/reports/assignments"}
                    icon={HiUsers}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/reports/course")}
                  >
                    {sidebarExpanded && "Course Report "}
                  </Sidebar.Item>
                <Sidebar.Item
                    as={Link}
                    to={"/admin/assignment-report"}
                    icon={MdOutlineAssessment}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/assignment-report")}
                  >
                    {sidebarExpanded && "Batch wise Assignment Report"}
                  </Sidebar.Item>
                <Sidebar.Item
                    as={Link}
                    to={"/admin/reports/assignments"}
                    icon={MdAssignment}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/reports/assignments")}
                  >
                    {sidebarExpanded && "Campus Assignment Leaderboard"}
                  </Sidebar.Item>
                {hasStaffModules && (
                  <Sidebar.Item
                      as={Link}
                      to={`/admin/reports/module/${staffCampusName}`}
                      icon={HiViewBoards}
                      onClick={handleNavItemClick}
                      className={getSidebarItemClass("/admin/reports/module")}
                    >
                      {sidebarExpanded && "Practice Lab"}
                    </Sidebar.Item>
                )}
                <Sidebar.Item
                    as={Link}
                    to={"/admin/student-details"}
                    icon={RiBarChartGroupedLine}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/student-details")}
                  >
                    {sidebarExpanded && "Comprehensive Report"}
                  </Sidebar.Item>
              </>
            )}
            {userRole === "bt-admin" && (
              <>
                {admin?.getAdminUsingEmail?.isSuperAdmin ? (
                  <>
                    <Sidebar.Item
                        as={Link}
                        to="/admin/courses"
                        icon={HiViewBoards}
                        onClick={handleNavItemClick}
                        className={getSidebarItemClass("/admin/courses")}
                      >
                        {sidebarExpanded && "Courses"}
                      </Sidebar.Item>
                    <Sidebar.Item
                        as={Link}
                        to={"/admin/assignment-folders"}
                        icon={MdAssignment}
                        onClick={handleNavItemClick}
                        className={getSidebarItemClass("/admin/assignment-folders")}
                      >
                        {sidebarExpanded && "Assignment Folders"}
                      </Sidebar.Item>
                    <Sidebar.Item
                        as={Link}
                        to="/admin/practice-lab"
                        icon={HiViewBoards}
                        onClick={handleNavItemClick}
                        className={getSidebarItemClass("/admin/practice-lab")}
                      >
                        {sidebarExpanded && "Practice Lab"}
                      </Sidebar.Item>
                    {isSuperAdminEmail && (
                      <>
                        <Sidebar.Item
                            as={Link}
                            to="/admin/feedback"
                            icon={MdMessage}
                            onClick={handleNavItemClick}
                            className={getSidebarItemClass("/admin/feedback")}
                          >
                            {sidebarExpanded && "Feedback"}
                          </Sidebar.Item>
                        <Sidebar.Item
                            as={Link}
                            to="/admin/deletion-requests"
                            icon={MdOutlineApproval}
                            onClick={handleNavItemClick}
                            className={getSidebarItemClass("/admin/deletion-requests")}
                          >
                            {sidebarExpanded && "Approval Request"}
                          </Sidebar.Item>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {admin?.getAdminUsingEmail?.permissions?.courses.length > 0 && (
                      <Sidebar.Item
                          as={Link}
                          to="/admin/courses"
                          icon={HiViewBoards}
                          onClick={handleNavItemClick}
                          className={getSidebarItemClass("/admin/courses")}
                        >
                          {sidebarExpanded && "Courses"}
                        </Sidebar.Item>
                    )}
                    {admin?.getAdminUsingEmail?.permissions?.assignments.length > 0 && (
                      <>
                        <Sidebar.Item
                            as={Link}
                            to="/admin/assignment-folders"
                            icon={MdAssignment}
                            onClick={handleNavItemClick}
                            className={getSidebarItemClass("/admin/assignment-folders")}
                          >
                            {sidebarExpanded && "Assignments"}
                          </Sidebar.Item>
                        <Sidebar.Item
                            as={Link}
                            to="/admin/practice-lab"
                            icon={HiViewBoards}
                            onClick={handleNavItemClick}
                            className={getSidebarItemClass("/admin/practice-lab")}
                          >
                            {sidebarExpanded && "Practice Lab"}
                          </Sidebar.Item>
                      </>
                    )}
                  </>
                )}

                <Sidebar.Item
                    as={Link}
                    to="/admin/clusters/campus-list"
                    icon={AiOutlineCluster}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/clusters")}
                  >
                    {sidebarExpanded && "Manage Cluster"}
                  </Sidebar.Item>

                {admin?.getAdminUsingEmail?.isSuperAdmin && (
                  <>
                    <Sidebar.Item
                        as={Link}
                        to="/admin/campus"
                        icon={FaBuildingColumns}
                        onClick={handleNavItemClick}
                        className={getSidebarItemClass("/admin/campus")}
                      >
                        {sidebarExpanded && "Manage Staff"}
                      </Sidebar.Item>

                    <Sidebar.Item
                        as={Link}
                        to="/admin/bt-admin"
                        icon={RiAdminLine}
                        onClick={handleNavItemClick}
                        className={getSidebarItemClass("/admin/bt-admin")}
                      >
                        {sidebarExpanded && "BT-Admin"}
                      </Sidebar.Item>
                    <Sidebar.Item
                        as={Link}
                        to="/admin/cronlogs"
                        icon={MdOutlineSchedule}
                        onClick={handleNavItemClick}
                        className={getSidebarItemClass("/admin/cronlogs")}
                      >
                        {sidebarExpanded && "Cron Logs"}
                      </Sidebar.Item>
                  </>
                )}
                {isSuperAdminEmail ? (
                  <Sidebar.Item
                      as={Link}
                      to="/admin/students"
                      icon={HiUser}
                      onClick={handleNavItemClick}
                      className={getSidebarItemClass("/admin/students")}
                    >
                      {sidebarExpanded && "Manage Students"}
                    </Sidebar.Item>
                ) : null}
              </>
            )}
          </Sidebar.ItemGroup>

          <Sidebar.ItemGroup>
            {userRole === "bt-admin" &&
              (admin?.getAdminUsingEmail?.isSuperAdmin ||
              admin?.getAdminUsingEmail?.permissions?.reports) ? (
                <Sidebar.Item
                    as={Link}
                    to="/admin/reports"
                    icon={HiChartPie}
                    onClick={handleNavItemClick}
                    className={getSidebarItemClass("/admin/reports")}
                  >
                    {sidebarExpanded && "Reports"}
                  </Sidebar.Item>
              ) : null}
          </Sidebar.ItemGroup>
        </Sidebar.Items>
      </Sidebar>
    </div>
  );
}

export default LeftSideBar;
